/**
 * Presentation metadata for each searchable entity type.
 *
 * One record per type, so the page renders every group the same way and a new
 * searchable entity is a data addition rather than another branch. The icon
 * and label match the navigation, so a "Startups" group reads as the same
 * thing the nav calls Startups.
 *
 * `attachment` has no detail route of its own — a file is reached through the
 * record it hangs off, and the search API returns the attachment's id, not its
 * parent's. So its `path` is null and the page renders those hits as plain
 * text rather than links. Better an honest dead-end than a link to a 404.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.11, PRD F6
 */
import type { SearchEntity } from '@nexus/contracts';

import type { IconName } from '../../components/ui/icon-names';
import {
  assetPath,
  contactPath,
  customerPath,
  pilotPath,
  startupPath,
} from '../../lib/routes';

export interface SearchEntityMeta {
  readonly icon: IconName;
  /** Key into the `search.group` translation namespace. */
  readonly labelKey: string;
  /** Builds the link to one hit, or null where the type has no detail page. */
  readonly path: ((id: string) => string) | null;
}

export const SEARCH_ENTITY_META: Record<SearchEntity, SearchEntityMeta> = {
  startup: { icon: 'target', labelKey: 'startups', path: startupPath },
  pilot: { icon: 'checklist', labelKey: 'pilots', path: pilotPath },
  customer: { icon: 'industry', labelKey: 'customers', path: customerPath },
  asset: { icon: 'pin', labelKey: 'assets', path: assetPath },
  contact: { icon: 'contract', labelKey: 'contacts', path: contactPath },
  attachment: { icon: 'data', labelKey: 'attachments', path: null },
};
